import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  CircularProgress,
  Chip,
  Alert,
  Typography,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import axios from 'axios';

type ApplicableFor = 'STUDENT' | 'STAFF' | 'BOTH';

interface DocumentModel {
  id: string;
  name: string;
  applicableFor: ApplicableFor;
  isMandatory: boolean;
  isActive: boolean;
}

const DocumentMaster: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    applicableFor: 'STUDENT' as ApplicableFor,
    isMandatory: false,
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/system-settings/document');
      setDocuments(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setViewMode(false);
    setFormData({
      name: '',
      applicableFor: 'STUDENT',
      isMandatory: false,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (doc: DocumentModel) => {
    setEditingId(doc.id);
    setViewMode(false);
    setFormData({
      name: doc.name,
      applicableFor: doc.applicableFor,
      isMandatory: doc.isMandatory,
      isActive: doc.isActive,
    });
    setDialogOpen(true);
  };

  const handleView = (doc: DocumentModel) => {
    setEditingId(doc.id);
    setViewMode(true);
    setFormData({
      name: doc.name,
      applicableFor: doc.applicableFor,
      isMandatory: doc.isMandatory,
      isActive: doc.isActive,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`/system-settings/document/${id}`);
      fetchDocuments();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete document');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/document/${id}/toggle-status`);
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === id ? { ...doc, isActive: !currentStatus } : doc
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchDocuments();
    }
  };

  const handleSave = async () => {
    try {
      if (editingId && !viewMode) {
        await axios.patch(`/system-settings/document/${editingId}`, formData);
      } else if (!editingId) {
        await axios.post('/system-settings/document', formData);
      }
      setDialogOpen(false);
      fetchDocuments();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save document');
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Document Master</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Document
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document Name</TableCell>
              <TableCell>Applicable For</TableCell>
              <TableCell>Mandatory</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No documents found.</TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>{doc.name}</TableCell>
                  <TableCell>{doc.applicableFor}</TableCell>
                  <TableCell>
                    {doc.isMandatory ? <Chip label="Yes" color="warning" size="small" /> : 'No'}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={doc.isActive}
                      onChange={() => handleToggleStatus(doc.id, doc.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(doc)}><VisibilityIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleEdit(doc)}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(doc.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{viewMode ? 'View Document' : editingId ? 'Edit Document' : 'New Document'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 320 }}>
            <TextField
              label="Document Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              disabled={viewMode}
            />
            <TextField
              select
              label="Applicable For"
              value={formData.applicableFor}
              onChange={(e) => setFormData({ ...formData, applicableFor: e.target.value as ApplicableFor })}
              fullWidth
              required
              disabled={viewMode}
            >
              <MenuItem value="STUDENT">Student</MenuItem>
              <MenuItem value="STAFF">Staff</MenuItem>
              <MenuItem value="BOTH">Both</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isMandatory}
                  onChange={(e) => setFormData({ ...formData, isMandatory: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Mandatory"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {!viewMode && <Button onClick={handleSave} variant="contained">Save</Button>}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DocumentMaster;
